import React from 'react';

/**
 * ChunkErrorBoundary — self-healing for the stale-SPA-chunk problem (Jose 2026-09-18).
 *
 * WHY: routes are code-split (React.lazy). When a device has a STALE cached index that
 * references chunk hashes hosting has since rotated, the lazy import 404s and throws a
 * ChunkLoadError. Suspense can't handle a throw, so the "Loading…" spinner hangs FOREVER
 * (this is exactly what stranded app.slikbarberlounge.com). versionGuard only reloads on
 * focus/visibility/online or a 90s idle poll — too slow, and it doesn't catch a chunk 404.
 *
 * This boundary catches the chunk error and hard-reloads ONCE (a fresh index → valid chunks).
 * sessionStorage guards against reload loops. Non-chunk errors are re-thrown unchanged, so
 * behavior for real app errors is identical to before (there was no boundary here previously).
 */

const RELOAD_KEY = 'slik_chunk_reload_ts';

function isChunkError(err: any): boolean {
  const s = `${err?.name || ''} ${err?.message || ''}`;
  return /ChunkLoadError|Loading chunk [\d]+ failed|Loading CSS chunk|Importing a module script failed|error loading dynamically imported module|failed to fetch dynamically imported module/i.test(s);
}

interface State { error: any | null; }

export class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: any): State {
    return { error };
  }

  componentDidCatch(err: any) {
    if (!isChunkError(err)) return; // real app error — leave it to bubble (see render)
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last > 20000) {            // don't loop: at most one auto-reload / 20s
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (!isChunkError(error)) throw error;     // non-chunk → propagate unchanged (no behavior change)
      // Chunk error: brief "updating" splash while the reload kicks in (or if the loop-guard held).
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3 text-center px-6">
            <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-600">Updating to the latest version…</p>
            <button onClick={() => window.location.reload()} className="text-sm text-purple-600 font-semibold underline">
              Reload now
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
